import classNames from 'classnames';
import reactUpdate from 'immutability-helper';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import * as ExternalFileDAO from 'dao/ExternalFileDAO';
import SignedExtFileURLProxy from 'external-files/SignedExtFileURLProxy';
import {ExternalFile, ExternalFileExcerpt} from 'notebook/util/NoteUtilConstants';
import notifications, {Response} from 'notifications';
import ErrorBanner from 'ui-library/banners/ErrorBanner';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as ModalContainer from 'ui-library/ModalContainer';
import Modal from 'ui-library/modals/Modal';
import {withLoadingScreen} from 'util/Promises';
import {InputChangeEvent} from 'util/Types';

interface FileExcerptModalProps {
  externalFile: ExternalFile;
  insertExcerptsHandler: (excerpts: Array<ExternalFileExcerpt>, {replace}: {replace: boolean}) => void;
  multiple: boolean;
  name: string;
  onRequestClose: () => void;
  owningEntityApiId: string;
}

interface State {
  error: string | null;
  excerpts: Array<Excerpt> | null;
  pages: string;
}

type Excerpt = ExternalFileExcerpt & {selected?: boolean};

class FileExcerptModal extends React.Component<FileExcerptModalProps, State> {
  static open(props: Omit<FileExcerptModalProps, 'onRequestClose'>): ModalContainer.Modal | null {
    return ModalContainer.open(
      FileExcerptModal,
      <FileExcerptModal
        // eslint-disable-next-line no-restricted-syntax
        {..._.extend(
          {
            onRequestClose(): void {
              ModalContainer.close(FileExcerptModal);
            },
          },
          props
        )}
      />
    );
  }

  static propTypes = {
    externalFile: PropTypes.object.isRequired,
    insertExcerptsHandler: PropTypes.func.isRequired,
    multiple: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    owningEntityApiId: PropTypes.string.isRequired,
  };

  _isMounted = false;

  componentDidMount(): void {
    this._isMounted = true;
    ExternalFileDAO.fetchExcerpts(this.props.externalFile.id).then(
      (excerpts: Array<ExternalFileExcerpt>) => {
        if (!this._isMounted) {
          return;
        }
        excerpts = this.getExcerptsFromPages(this.state.pages, excerpts);
        this.setState({excerpts});
      },
      (response: Response) => {
        if (!this._isMounted) {
          return;
        }
        const error = notifications.getFriendlyError(response);
        if (!error) {
          notifications.notifyError(response);
        }
        this.setState({excerpts: [], error});
      }
    );
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  static defaultProps = {
    multiple: true,
  };

  state: State = {
    excerpts: null,
    pages: '',
    error: null,
  };

  selectedExcerpts(): Array<Excerpt> {
    if (this.state.excerpts == null) {
      return [];
    }
    return this.state.excerpts.filter((excerpt) => excerpt.selected);
  }

  save = async (): Promise<void> => {
    const selectedOffsets = this.selectedExcerpts().map((a) => a.referenceOffset);
    const highResExcerpts = await withLoadingScreen(
      ExternalFileDAO.fetchExcerpts(this.props.externalFile.id, selectedOffsets),
      'Preparing excerpt...'
    );
    this.props.insertExcerptsHandler(highResExcerpts, {replace: this.isReplacing()});
    this.props.onRequestClose();
  };

  renderWithSignedURL = (signedURL: string, excerpt: Excerpt): JSX.Element => {
    if (signedURL === undefined) {
      return <p className="u-spacing-top faded text-center">Loading...</p>;
    } else if (signedURL == null) {
      return <p className="u-spacing-top faded text-center">Error loading file</p>;
    }
    return (
      <img
        src={signedURL}
        alt="thumbnail"
        className={classNames({
          'excerpt-preview u-tiny-spacing-left u-pointer': true,
          selected: excerpt.selected,
        })}
        onClick={() => {
          this.handleSelectExcerpt(excerpt);
        }}
      />
    );
  };

  handleSelectExcerpt(excerpt: Excerpt): void {
    const index = this.state.excerpts!.indexOf(excerpt);
    let excerpts = reactUpdate(this.state.excerpts, {[index]: {$merge: {selected: !excerpt.selected}}})!;
    if (!this.props.multiple) {
      // Deselect all the other excerpts.
      excerpts = excerpts.map((ex, i) => {
        if (i === index) {
          return ex;
        } else {
          return reactUpdate(ex, {$merge: {selected: false}});
        }
      });
    }

    function getPageRange(start: number, end: number): string {
      if (start === end - 1) {
        return `${start + 1}`;
      } else {
        return `${start + 1}-${end}`;
      }
    }

    // Convert the selected excerpts into user friendly page numbers (1, 3-6, 8-10)
    const pages = [];
    let start = null;
    for (let i = 0; i < excerpts.length; i++) {
      excerpt = excerpts[i];
      if (excerpt.selected) {
        if (start == null) {
          start = i;
        }
      } else if (start != null) {
        pages.push(getPageRange(start, i));
        start = null;
      }
    }
    if (start != null) {
      pages.push(getPageRange(start, excerpts.length));
    }

    this.setState({excerpts, pages: pages.join(', ')});
  }

  renderExcerpt(excerpt: Excerpt): JSX.Element {
    return (
      <SignedExtFileURLProxy
        url={excerpt.excerptFile.url}
        externalFileId={excerpt.excerptFile.id}
        renderWithSignedURL={(signedURL: string) => this.renderWithSignedURL(signedURL, excerpt)}
        useRedirectURL={false}
        key={excerpt.id}
        owningEntityApiId={this.props.owningEntityApiId}
      />
    );
  }

  getExcerptsFromPages(pages: string, excerpts: Array<Excerpt>): Array<Excerpt> {
    let page;
    const selected: {[index: number]: boolean} = {};
    if (this.props.multiple) {
      // Do a best effort matching from user text to selected pages.
      // Supports text like '1,3-5, 7, 10 - 15'
      for (page of Array.from(pages.split(','))) {
        let end;
        let match;
        let start;
        page = page.replace(/\s/g, '');
        match = /^(\d+)-(\d+)$/.exec(page);
        if (match) {
          // 3-5
          start = parseInt(match[1], 10);
          end = parseInt(match[2], 10);
        } else {
          match = /^(\d+)(-)?$/.exec(page);
          if (match) {
            // 3 or 3-
            start = parseInt(match[1], 10);
            end = start;
          } else {
            continue;
          }
        }

        if (start <= end) {
          for (let index = start; index <= end; index++) {
            // since user input is 1-indexed.
            selected[index - 1] = true;
          }
        }
      }
    } else {
      page = parseInt(pages, 10);
      if (!Number.isNaN(page)) {
        selected[page - 1] = true; // since user input is 1-indexed.
      }
    }

    return excerpts.map((excerpt, i) => ({
      ...excerpt,
      selected: selected[i] || false,
    }));
  }

  onPagesChange = (e: InputChangeEvent): void => {
    let excerpts;
    const pages = e.target.value;
    if (this.state.excerpts != null) {
      excerpts = this.getExcerptsFromPages(pages, this.state.excerpts);
    } else {
      excerpts = null;
    }
    this.setState({excerpts, pages});
  };

  // The user is replacing the excerpt, not inserting new ones.
  isReplacing(): boolean {
    return !this.props.multiple;
  }

  renderExcerpts(): JSX.Element {
    if (!this.state.excerpts) {
      return <CenteredHorizontalThrobber />;
    } else if (this.state.excerpts.length === 0) {
      return <div className="text-center u-small-spacing-top faded">No excerpts could be found.</div>;
    } else {
      return <>{Array.from(this.state.excerpts).map((excerpt, i) => this.renderExcerpt(excerpt))}</>;
    }
  }

  renderBody(): JSX.Element {
    return (
      <div>
        <p>
          {`Select the ${this.props.multiple ? 'pages' : 'page'} to \
    ${!this.isReplacing() ? 'insert' : 'replace with'}.`}
        </p>
        <Row>
          <Col sm={4} className="u-input-line-height">
            {`Type in ${this.props.multiple ? 'page numbers' : 'a page number'}: `}
          </Col>
          <Col sm={8}>
            <input
              autoFocus={true}
              type="text"
              placeholder={this.props.multiple ? '1, 3-5, 7' : '1'}
              value={this.state.pages}
              onChange={this.onPagesChange}
            />
          </Col>
        </Row>
        <div className="u-spacing-top">
          <div className="u-tiny-spacing-bottom">
            Or select the {this.props.multiple ? 'pages' : 'page'} directly:
          </div>
          {this.state.error ? (
            <ErrorBanner>{this.state.error}</ErrorBanner>
          ) : (
            <div className="excerpts-container">{this.renderExcerpts()}</div>
          )}
        </div>
      </div>
    );
  }

  render(): JSX.Element {
    return (
      <Modal
        title={`${!this.isReplacing() ? 'Insert' : 'Replace'} \
  ${this.props.multiple ? 'excerpts' : 'excerpt'} from "${this.props.name}"`}
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={this.save}
        buttons={[
          {
            text: !this.isReplacing() ? 'Insert' : 'Replace',
            submit: true,
            disabled: this.state.excerpts == null || this.selectedExcerpts().length === 0,
          },
        ]}
      >
        {this.renderBody()}
      </Modal>
    );
  }
}

export default FileExcerptModal;
